import { IvrHoursSetId } from '@bariendo/lib/constants';
import { IvrDigit, IvrHoursSet, IvrKeyMapping, isHoursSetOpen } from '@bariendo/lib/ivr';
import {
  EMPTY_IVR_ROUTE_AVAILABILITY,
  ResolvedRouteStep,
  RouteStepOutcome,
  RouteTestCaller,
  RouteTestStatus,
  collectRouteTestProviderRefs,
  collectRouteTestRoles,
  resolveRouteTest,
} from '@bariendo/lib/ivr-route-test';
import { Alert, Badge, Card, Group, Loader, Select, Stack, Text } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { createReference, formatHumanName, getReferenceString } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { ResourceInput } from '@medplum/react';
import { IconAlertTriangle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { ReactElement, ReactNode, useMemo, useState } from 'react';
import { useIvrRouteAvailability } from '../hooks/useIvrRouteAvailability';

const UNKNOWN_CALLER_NAME = 'Unknown number';

/** Beyond this the live Flag reads stop describing the moment being tested. */
const LIVE_FLAG_TOLERANCE_MINUTES = 15;

interface IVRRouteTesterProps {
  /** The template being edited, for the "Matched as" line. */
  menuLabel: string;
  /** The chains currently in the form — not what is saved. */
  keyMappings: IvrKeyMapping[];
  hoursSets: Map<IvrHoursSetId, IvrHoursSet>;
  languageSwitchKey: number;
}

function OutputRow({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <Group gap="md" align="flex-start" wrap="nowrap">
      <Text size="sm" c="dimmed" w={150} style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </Group>
  );
}

function StepValue({ step }: { step: ResolvedRouteStep }): ReactElement {
  const names = step.ringing.map((provider) => provider.name).join(', ');

  switch (step.outcome) {
    case RouteStepOutcome.REACHED:
      return (
        <Group gap="xs" align="center" wrap="wrap">
          <Text size="sm" fw={500}>
            {names ? `${step.label} · ${names}` : step.label}
          </Text>
          <Badge size="sm" variant="light" color={step.detail ? 'yellow' : 'green'}>
            {step.detail ?? 'available'}
          </Badge>
        </Group>
      );
    case RouteStepOutcome.NOT_REACHED:
      return (
        <Text size="sm" c="dimmed">
          {step.label} — not reached
        </Text>
      );
    case RouteStepOutcome.ENDS_CALL:
      return <Text size="sm">{step.label}</Text>;
    case RouteStepOutcome.NO_CALLER:
    case RouteStepOutcome.SKIPPED:
      return (
        <Group gap="xs" align="center" wrap="wrap">
          <Text size="sm" c="dimmed">
            {step.label}
          </Text>
          <Badge size="sm" variant="light" color={step.outcome === RouteStepOutcome.NO_CALLER ? 'orange' : 'red'}>
            {step.detail}
          </Badge>
        </Group>
      );
  }
}

/** "Destination" for the rung that rings, counting fallbacks after it and rungs before it. */
function stepRowLabel(steps: ResolvedRouteStep[], index: number): string {
  const destination = steps.findIndex((step) => step.outcome === RouteStepOutcome.REACHED);

  if (index === destination) {
    return 'Destination';
  }

  return destination >= 0 && index > destination ? `Fallback ${index - destination}` : `Rung ${index + 1}`;
}

/**
 * The route tester (BAR-2624): caller · key · time, and the path the chain in the form
 * resolves to.
 *
 * Reads the form, never the saved template, so a chain can be checked before it is saved.
 * Purely simulated — nothing here places a call or writes a resource.
 */
export function IVRRouteTester({
  menuLabel,
  keyMappings,
  hoursSets,
  languageSwitchKey,
}: IVRRouteTesterProps): ReactElement {
  const [callerPatient, setCallerPatient] = useState<Patient | undefined>();
  const [at, setAt] = useState<Date>(() => new Date());
  // Undefined until the admin picks one. The mappings arrive after the first render, so a
  // lazily initialised default would be stuck on whatever the empty form implied.
  const [pickedDigit, setPickedDigit] = useState<IvrDigit | undefined>();

  const roles = useMemo(() => collectRouteTestRoles(keyMappings), [keyMappings]);
  const providers = useMemo(() => collectRouteTestProviderRefs(keyMappings), [keyMappings]);

  // Memoised: a fresh object every render would re-fire the resolve below on every keystroke
  // anywhere on the page.
  const caller: RouteTestCaller = useMemo(
    () =>
      callerPatient
        ? {
            reference: getReferenceString(createReference(callerPatient)),
            name: formatHumanName(callerPatient.name?.[0]) || UNKNOWN_CALLER_NAME,
          }
        : { name: UNKNOWN_CALLER_NAME },
    [callerPatient]
  );

  const { availability, loading, error } = useIvrRouteAvailability({
    patient: caller.reference,
    roles,
    providers,
    at,
  });

  const selectableDigits = useMemo(
    () => Array.from({ length: 10 }, (_, index) => index as IvrDigit).filter((option) => option !== languageSwitchKey),
    [languageSwitchKey]
  );

  const digitOptions = useMemo(
    () =>
      selectableDigits.map((option) => ({
        value: String(option),
        label: keyMappings.some((item) => item.digit === option)
          ? `Pressed ${option}`
          : `Pressed ${option} · unconfigured`,
      })),
    [keyMappings, selectableDigits]
  );

  // Opens on the first configured option, which is the one an admin is most likely to be
  // asking about — and never on the reserved language-switch digit.
  const digit =
    pickedDigit ??
    [...keyMappings]
      .map((item) => item.digit)
      .sort((a, b) => a - b)
      .find((option) => option !== languageSwitchKey) ??
    selectableDigits[0];

  const mapping = keyMappings.find((item) => item.digit === digit);
  const hoursSet = mapping?.hoursSetId ? hoursSets.get(mapping.hoursSetId) : undefined;
  const isAfterHours = !!mapping?.hoursSetId && !(hoursSet && isHoursSetOpen(hoursSet, dayjs(at)));

  const result = useMemo(
    () => resolveRouteTest({ digit, menuLabel, mapping, caller, isAfterHours, availability }),
    [digit, menuLabel, mapping, caller, isAfterHours, availability]
  );

  const flagsAreStale = Math.abs(dayjs(at).diff(dayjs(), 'minute')) > LIVE_FLAG_TOLERANCE_MINUTES;

  // Without this the strip flashes "nobody" on first paint: the empty snapshot makes every
  // rung look unavailable, which is the one thing the tester must never say by accident.
  // A disabled or unconfigured option does not depend on the snapshot and shows straight away.
  const awaitingSnapshot =
    loading && availability === EMPTY_IVR_ROUTE_AVAILABILITY && result.status === RouteTestStatus.RESOLVED;

  return (
    <Card withBorder padding="md">
      {/* The section title lives on the page, beside "Key mappings" — repeating it here
          would state the same fact twice. Only the caveat stays. */}
      <Group justify="flex-end" align="center" mb="sm">
        <Text size="xs" c="dimmed">
          Simulated — nobody is rung.
        </Text>
      </Group>

      <Group grow align="flex-start" gap="md">
        <ResourceInput
          name="route-tester-caller"
          resourceType="Patient"
          label="Caller"
          placeholder={UNKNOWN_CALLER_NAME}
          defaultValue={undefined}
          onChange={(resource) => setCallerPatient((resource as Patient) ?? undefined)}
        />
        <Select
          label="Key"
          data={digitOptions}
          value={String(digit)}
          allowDeselect={false}
          onChange={(value) => setPickedDigit(Number(value) as IvrDigit)}
        />
        <DateTimePicker
          label="Time"
          valueFormat="ddd MMM D, h:mma"
          value={at}
          onChange={(value) => setAt(value ? new Date(value) : new Date())}
        />
      </Group>

      <Stack gap="xs" mt="md">
        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {error}
          </Alert>
        )}

        <OutputRow label="Matched as">
          <Group gap="xs">
            <Text size="sm">{result.matchedAs}</Text>
            {loading && <Loader size="xs" />}
          </Group>
        </OutputRow>

        {!awaitingSnapshot && result.nobodyMessage && (
          <OutputRow label="Result">
            <Text size="sm" fw={500} c="red.7">
              {result.nobodyMessage}
            </Text>
          </OutputRow>
        )}

        {!awaitingSnapshot &&
          result.steps.map((step, index) => (
            <OutputRow key={`${step.label}-${index}`} label={stepRowLabel(result.steps, index)}>
              <StepValue step={step} />
            </OutputRow>
          ))}

        {!awaitingSnapshot && result.notifiedTeams.length > 0 && (
          <OutputRow label="If nobody answers">
            <Stack gap={6}>
              {result.notifiedTeams.map((notified) => (
                <div key={notified.team}>
                  <Text size="sm">Open row for {notified.team}</Text>
                  {notified.whyLine && (
                    <Text size="xs" c="dimmed" fs="italic">
                      &ldquo;{notified.whyLine}&rdquo;
                    </Text>
                  )}
                </div>
              ))}
            </Stack>
          </OutputRow>
        )}

        {flagsAreStale && (
          <Text size="xs" c="dimmed">
            Do-not-disturb and in-call state is read as of now, not as of the tested time.
          </Text>
        )}
      </Stack>
    </Card>
  );
}

export default IVRRouteTester;
